import { Routes } from '@/CSIRouter';
import profileIcon from '@/assets/icons/profile-icon.png';
import LogoutButton from '@/auth/LogoutButton';
import { useAuth } from '@/auth/useAuth';
import { OrganizationSwitcher } from '@/organization/OrganizationSwitcher';
import Group from '@mui/icons-material/Groups';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonIcon from '@mui/icons-material/Person';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import {
  Box,
  Divider,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';

interface UserMenuProps {
  logoHeight?: number | string;
}

export const UserMenu: React.FC<UserMenuProps> = ({ logoHeight = 40 }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const { user } = useAuth();

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <IconButton
        onClick={handleOpen}
        sx={{
          p: 0,
          '&:hover': {
            '& img': {
              transform: 'scale(1.05)',
            },
          },
        }}
      >
        <img
          src={profileIcon}
          alt='Profile'
          style={{
            width: logoHeight,
            height: logoHeight,
            objectFit: 'contain',
            transition: 'transform 0.2s ease',
          }}
        />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{
          elevation: 0,
          sx: {
            mt: 2,
            minWidth: 280,
            borderRadius: 1,
            overflow: 'visible',
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            bgcolor: 'background.paper',
            '& .MuiList-root': {
              p: 0.5,
              backgroundColor: 'background.paper',
            },
            '& .MuiDivider-root': {
              borderColor: 'divider',
              margin: 0,
            },
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 20,
              width: 12,
              height: 12,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
              border: '1px solid',
              borderColor: 'divider',
              borderBottom: 'none',
              borderRight: 'none',
            },
          },
        }}
      >
        {/* User Info Header */}
        <Box
          sx={{
            px: 3,
            py: 2,
            bgcolor: 'action.selected',
            borderRadius: '4px 4px 0 0',
          }}
        >
          <Typography variant='subtitle2' fontWeight={600}>
            {user?.firstName && user?.lastName
              ? `${user.firstName} ${user.lastName}`
              : user?.email || 'User'}
          </Typography>
        </Box>

        <Divider
          sx={{
            opacity: 0.6,
            my: 0,
            borderColor: 'divider',
            backgroundColor: 'transparent',
          }}
        />

        <MenuItem
          component={Link}
          to='profile'
          sx={{
            py: 2,
            px: 3,
            textDecoration: 'none',
            color: 'inherit',
            '&:hover': {
              bgcolor: 'action.selected',
            },
            transition: 'background-color 0.2s ease',
          }}
        >
          <ListItemIcon sx={{ minWidth: 40 }}>
            <PersonIcon fontSize='small' sx={{ color: '#7BBCB3' }} />
          </ListItemIcon>
          <Typography variant='body2'>My Profile</Typography>
        </MenuItem>
        <MenuItem
          component={Link}
          to='users'
          sx={{
            py: 2,
            px: 3,
            textDecoration: 'none',
            color: 'inherit',
            '&:hover': {
              bgcolor: 'action.selected',
            },
            transition: 'background-color 0.2s ease',
          }}
        >
          <ListItemIcon sx={{ minWidth: 40 }}>
            <Group fontSize='small' sx={{ color: '#7BBCB3' }} />
          </ListItemIcon>
          <Typography variant='body2'>Organization Details</Typography>
        </MenuItem>
        <MenuItem
          sx={{
            py: 2,
            px: 3,
            '&:hover': {
              bgcolor: 'action.selected',
            },
            transition: 'background-color 0.2s ease',
          }}
        >
          <ListItemIcon sx={{ minWidth: 40 }}>
            <SwapHorizIcon fontSize='small' sx={{ color: '#7BBCB3' }} />
          </ListItemIcon>
          <OrganizationSwitcher />
        </MenuItem>
        {user?.isSuperuser && (
          <MenuItem
            component={Link}
            to={`${Routes.Root.Admin.path}/${Routes.Root.Admin.Organizations.path}`}
            onClick={handleClose}
            sx={{
              py: 2,
              px: 3,
              textDecoration: 'none',
              color: 'inherit',
              '&:hover': {
                bgcolor: 'action.selected',
              },
              transition: 'background-color 0.2s ease',
            }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              <Group fontSize='small' sx={{ color: '#7BBCB3' }} />
            </ListItemIcon>
            <Typography variant='body2'>Manage All Orgs</Typography>
          </MenuItem>
        )}

        <Divider
          sx={{
            my: 0,
            opacity: 0.6,
            borderColor: 'divider',
            backgroundColor: 'transparent',
          }}
        />

        <MenuItem
          sx={{
            py: 2,
            px: 3,
            '&:hover': {
              bgcolor: 'rgba(255, 152, 0, 0.08)',
            },
            transition: 'background-color 0.2s ease',
            borderRadius: '0 0 4px 4px',
          }}
        >
          <ListItemIcon sx={{ minWidth: 40 }}>
            <LogoutIcon fontSize='small' sx={{ color: '#FF9800' }} />
          </ListItemIcon>
          <LogoutButton />
        </MenuItem>
      </Menu>
    </>
  );
};
